import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

// Fix K (2026-09-28): selector de servicios compartido por «Nueva orden» e Intake.
// Primero se elige el tipo de trabajo (turnover / proyecto / remediation) y después el servicio,
// con buscador y agrupado por categoría. El catálogo tiene más de 100 ítems: una lista plana
// sin buscador no sirve. Desde acá mismo se puede dar de alta un servicio que no existe.
export const TIPOS_TRABAJO = ["turnover", "proyecto", "remediation"];

const CATEGORIA_LABELS = {
  pintura: "Pintura", limpieza: "Limpieza", pisos: "Pisos", plomeria: "Plomería",
  electricidad: "Electricidad", vidrios: "Vidrios", metal: "Metal", amenidades: "Amenidades",
  obra: "Obra", emergencia: "Emergencia", turnover: "Turnover", hvac: "HVAC", general: "General",
  adicional: "Adicional",
};
const categoriaLabel = (cat) => CATEGORIA_LABELS[cat] || (cat.charAt(0).toUpperCase() + cat.slice(1));

const tipoDe = (s) => s?.tipo_trabajo || "turnover";

export const ServicePicker = ({
  services, value, onChange, conTarifa = null, disabled = false, testId = "service-picker", small = false,
}) => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [creando, setCreando] = useState(false);
  const lista = useMemo(
    () => (services || []).filter((s) => s.activo !== false && s.status !== "inactive"),
    [services]
  );
  const seleccionado = lista.find((s) => s.id === value);
  const [tipo, setTipo] = useState(tipoDe(seleccionado));

  // Si el servicio viene elegido de antes (borrador de IA, turnover rápido), la pestaña de tipo
  // se acomoda sola al tipo de ese servicio.
  useEffect(() => {
    if (seleccionado) setTipo(tipoDe(seleccionado));
  }, [seleccionado]);

  const grupos = useMemo(() => {
    const porCat = {};
    for (const s of lista) {
      if (tipoDe(s) !== tipo) continue;
      const cat = s.categoria || "general";
      (porCat[cat] = porCat[cat] || []).push(s);
    }
    const sinPrecio = (s) => (conTarifa && !conTarifa.has(s.id) ? 1 : 0);
    return Object.entries(porCat)
      .map(([cat, items]) => [cat, items.sort(
        (a, b) => sinPrecio(a) - sinPrecio(b) || a.nombre.localeCompare(b.nombre))])
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [lista, tipo, conTarifa]);

  const cambiarTipo = (nuevo) => {
    setTipo(nuevo);
    if (seleccionado && tipoDe(seleccionado) !== nuevo) onChange("");
  };

  // Fix P (2026-09-29): si lo que se escribió ya es un servicio (sin importar mayúsculas), no se
  // ofrece «+ Agregar»: se elige de la lista. Antes aparecían las dos opciones juntas.
  const nombreBuscado = busqueda.trim().toLowerCase();
  const existeExacto = !!nombreBuscado &&
    (services || []).some((s) => s.nombre.trim().toLowerCase() === nombreBuscado);
  const puedeCrear = can("service_types", "create") && !existeExacto;

  const crear = async () => {
    const nombre = busqueda.trim();
    if (nombre.length < 3) {
      toast.error(t("wo.form.new_service_type_name"));
      return;
    }
    const existente = (services || []).find((s) => s.nombre.trim().toLowerCase() === nombre.toLowerCase());
    if (existente) {
      onChange(existente.id);
      setOpen(false);
      return;
    }
    setCreando(true);
    try {
      const { data } = await api.post("/service-types", {
        nombre, categoria: "general", tipo_trabajo: tipo,
      });
      await qc.invalidateQueries({ queryKey: ["service-types"] });
      onChange(data.id);
      setBusqueda("");
      setOpen(false);
      toast.success(t("wo.form.new_service_created", { name: nombre }));
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setCreando(false);
    }
  };

  return (
    <div>
      <div className="mt-1 flex gap-1" data-testid={`${testId}-types`}>
        {TIPOS_TRABAJO.map((tp) => (
          <button
            key={tp}
            type="button"
            disabled={disabled}
            data-testid={`${testId}-type-${tp}`}
            onClick={() => cambiarTipo(tp)}
            className={cn(
              "rounded-[6px] border px-2 py-0.5 text-[11.5px] transition-colors",
              tipo === tp
                ? "border-brand bg-brand-soft font-medium text-brand"
                : "border-line text-ink-secondary hover:bg-surface-sunken"
            )}
          >
            {t(`wo.form.work_types.${tp}`)}
          </button>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled}
            data-testid={testId}
            className={cn(
              "mt-1 w-full justify-between rounded-[6px] font-normal",
              small ? "h-8 text-[12.5px]" : "h-9 text-[13px]"
            )}
          >
            <span className={cn("truncate", !seleccionado && "text-ink-tertiary")}>
              {seleccionado?.nombre || t("wo.form.service_ph")}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
          <Command>
            <CommandInput
              value={busqueda}
              onValueChange={setBusqueda}
              placeholder={t("wo.form.service_search")}
              className="text-[13px]"
            />
            {puedeCrear && (
              <button
                type="button"
                data-testid={`${testId}-create`}
                disabled={creando}
                onClick={crear}
                className="flex w-full items-center gap-1.5 border-b border-line px-3 py-2 text-left text-[12.5px] font-medium text-brand hover:bg-surface-sunken"
              >
                {creando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                {busqueda.trim()
                  ? t("wo.form.new_service_named", { name: busqueda.trim() })
                  : t("wo.form.new_service")}
              </button>
            )}
            <CommandList>
              <CommandEmpty className="py-4 text-center text-[12.5px] text-ink-tertiary">
                {t("wo.form.service_empty")}
              </CommandEmpty>
              {grupos.map(([categoria, items]) => (
                <CommandGroup key={categoria} heading={categoriaLabel(categoria)}>
                  {items.map((s) => (
                    <CommandItem
                      key={s.id}
                      value={s.nombre}
                      onSelect={() => { onChange(s.id); setBusqueda(""); setOpen(false); }}
                      className="text-[13px]"
                    >
                      <Check className={cn("mr-2 h-3.5 w-3.5 shrink-0",
                        value === s.id ? "opacity-100" : "opacity-0")} />
                      <span className="truncate">{s.nombre}</span>
                      {/* «sin tarifa» solo tiene sentido en turnover: proyectos y remediation se
                          cotizan a medida, no tienen lista de precios. */}
                      {tipo === "turnover" && conTarifa && !conTarifa.has(s.id) && (
                        <span className="ml-auto shrink-0 pl-2 text-[11px] text-ink-tertiary">
                          {t("wo.form.no_rate_tag")}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
